import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiTv } from 'react-icons/fi';
import xtreamAPI, { XtreamContent, UICategory } from '../../api/xtreamAPI';
import { getCachedLiveStreams } from '../../utils/liveStreamsCache';
import { getXtreamServerUrl, useAuth } from '../../contexts/AuthContext';
import { useDevice } from '../../contexts/DeviceContext';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { useParentalContentLocks } from '../../hooks/useParentalContentLocks';
import { useLockToggle } from '../../hooks/useLockToggle';
import { isKidsCategory } from '../../utils/kidsCategories';
import { DEMO_CHANNELS, DEMO_CATEGORY } from '../../data/demoChannels';
import { useLiveHistory } from '../../contexts/LiveHistoryContext';
import { useFavorites } from '../../hooks/useFavorites';
import { useContentNavIds } from '../../hooks/useContentNavIds';
import { buildHomeChannelList } from '../../utils/homeChannelOrder';
import { TopNavbar, NAV_ICON_SEARCH_INDEX, NAV_ICON_FAVORITES_INDEX, NAV_ICON_SETTINGS_INDEX, NAV_ICON_LOCK_INDEX, NAV_ICON_HISTORY_INDEX, NAV_ICON_REFRESH_INDEX } from '../../components/TopNavbar/TopNavbar';
import { ContentNav } from '../../components/ContentNav/ContentNav';
import { ChannelList } from '../../components/ChannelList/ChannelList';
import { EpgBar } from '../../components/EpgBar/EpgBar';
import PasswordModal from '../../components/PasswordModal/PasswordModal';
import './Home.css';

const HOME_NAV_ACTIONS = [
  NAV_ICON_SEARCH_INDEX,
  NAV_ICON_FAVORITES_INDEX,
  NAV_ICON_SETTINGS_INDEX,
  NAV_ICON_LOCK_INDEX,
  NAV_ICON_HISTORY_INDEX,
  NAV_ICON_REFRESH_INDEX,
];

interface HomeProps {
  isDemo?: boolean;
  onOpenLiveTV?: (entryState: {
    selectedChannel: XtreamContent | null;
    channels: XtreamContent[];
    categories: UICategory[];
  }) => void;
  onPreviewLiveTV?: (entryState: {
    selectedChannel: XtreamContent | null;
    channels: XtreamContent[];
    categories: UICategory[];
  }) => void;
  onOpenFavorites?: () => void;
  onOpenMovies?: () => void;
  onOpenSeries?: () => void;
  onOpenKids?: () => void;
  onOpenAnime?: () => void;
  onOpenSports?: () => void;
  onOpenSettings?: () => void;
  onOpenSearch?: () => void;
  onOpenHistory?: () => void;
  onUpdateContent?: () => void;
  onRequestExit?: () => void;
  /** When true (e.g. Settings overlay is open), the keydown listener is suspended */
  paused?: boolean;
}

export const Home: React.FC<HomeProps> = ({ isDemo, onOpenLiveTV, onPreviewLiveTV, onOpenFavorites, onOpenMovies, onOpenSeries, onOpenKids, onOpenAnime, onOpenSports, onOpenSettings, onOpenSearch, onOpenHistory, onUpdateContent, onRequestExit, paused }) => {
  const { t } = useTranslation();
  const { authData } = useAuth();
  const { isDemo: contextIsDemo } = useDevice();
  const { isKidsMode } = useParentalControls();
  const { isContentRestricted } = useParentalContentLocks();
  const {
    handleLockIconClick,
    handleLockPasswordConfirm,
    handleLockPasswordCancel,
    lockModalOpen,
    lockModalMode,
    lockModalError,
  } = useLockToggle();
  const effectiveDemo = isDemo ?? contextIsDemo;
  const { items: liveHistoryItems } = useLiveHistory();
  const { items: favoriteItems } = useFavorites();

  const [rawChannels, setRawChannels] = useState<XtreamContent[]>([]);
  const [categories, setCategories] = useState<UICategory[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<XtreamContent | null>(null);
  const [focusedChannelIndex, setFocusedChannelIndex] = useState(0);
  const [activeNavItem, setActiveNavItem] = useState('tv');
  const [focusZone, setFocusZone] = useState<'topnav' | 'contentnav' | 'channels'>('channels');
  const [topnavFocusedIdx, setTopnavFocusedIdx] = useState(0);
  const [contentnavFocusedId, setContentnavFocusedId] = useState('tv');
  const contentNavIds = useContentNavIds();
  const loadedAuthKeyRef = useRef<string>('');

  // Ref updated synchronously on every render so keydown handler always sees current value
  const pausedRef = useRef(paused ?? false);
  pausedRef.current = paused ?? false;

  const favoriteIds = useMemo(
    () => new Set(favoriteItems.filter((item) => item.type === 'live').map((item) => item.id)),
    [favoriteItems]
  );

  const lastWatchedId = useMemo(() => {
    const latest = [...liveHistoryItems].sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)[0] || null;
    return String(latest?.streamId || '');
  }, [liveHistoryItems]);

  const watchedAtByStreamId = useMemo(() => {
    const watchedAt = new Map<string, number>();
    liveHistoryItems.forEach((item) => {
      const streamId = String(item.streamId || '');
      if (streamId) watchedAt.set(streamId, item.lastWatchedAt);
    });
    return watchedAt;
  }, [liveHistoryItems]);

  const channels = useMemo(() => {
    const categoryOrder = new Map<string, number>();
    categories.forEach((category, index) => {
      categoryOrder.set(String(category.id || ''), index);
    });

    // In kids mode, restrict to channels from kids categories only
    const kidsCategoryIds = isKidsMode
      ? new Set(categories.filter(c => isKidsCategory(c.name)).map(c => String(c.id || '')))
      : null;
    const kidsFilteredChannels = kidsCategoryIds
      ? rawChannels.filter(ch => kidsCategoryIds.has(String(ch.category_id || '')))
      : rawChannels;
    const sourceChannels = kidsFilteredChannels.filter((channel) => !isContentRestricted(
      'live',
      String(channel.stream_id || ''),
      String(channel.category_id || '')
    ));

    const categorySortedChannels = sourceChannels.slice().sort((left, right) => {
      const leftCategoryOrder = categoryOrder.get(String(left.category_id || '')) ?? Number.MAX_SAFE_INTEGER;
      const rightCategoryOrder = categoryOrder.get(String(right.category_id || '')) ?? Number.MAX_SAFE_INTEGER;

      if (leftCategoryOrder !== rightCategoryOrder) {
        return leftCategoryOrder - rightCategoryOrder;
      }

      return (left.name || '').localeCompare(right.name || '');
    });

    const seenStreamIds = new Set<string>();
    const uniqueChannels = categorySortedChannels.filter((channel) => {
      const id = String(channel.stream_id || '');
      if (!id || seenStreamIds.has(id)) return false;
      seenStreamIds.add(id);
      return true;
    });

    return uniqueChannels;
  }, [categories, isContentRestricted, isKidsMode, rawChannels]);

  const { channels: homeChannels, favoriteCount } = useMemo(
    () => buildHomeChannelList(channels, favoriteIds, watchedAtByStreamId),
    [channels, favoriteIds, watchedAtByStreamId]
  );
  const visibleSelectedChannel = selectedChannel && homeChannels.some(
    (channel) => String(channel.stream_id || '') === String(selectedChannel.stream_id || '')
  ) ? selectedChannel : homeChannels[0] || null;

  // Demo mode: inject built-in channels (bypass Xtream)
  useEffect(() => {
    if (!effectiveDemo) return;
    setRawChannels(DEMO_CHANNELS);
    setCategories([DEMO_CATEGORY]);
    setLoadingChannels(false);
  }, [effectiveDemo]);

  // Configura credenciais do servidor Xtream e carrega canais
  useEffect(() => {
    if (effectiveDemo) return; // demo mode channels come from the effect above
    if (!authData) return;

    const { server, user } = authData;
    const host = getXtreamServerUrl(server);
    const authKey = `${host}|${user.username}`;

    if (loadedAuthKeyRef.current === authKey) {
      return;
    }

    loadedAuthKeyRef.current = authKey;
    xtreamAPI.setCredentials(host, user.username, user.password, server.server_protocol);

    let cancelled = false;
    setLoadingChannels(true);

    const loadHomeChannels = async () => {
      try {
        const allChannels = await getCachedLiveStreams();
        if (cancelled) return;

        const categories = await xtreamAPI.getLiveCategoriesForUI();
        if (cancelled) return;
        setRawChannels(allChannels);
        setCategories(categories);
      } catch {
        // swallow network errors
      } finally {
        // Always clear spinner — even if cancelled by a new auth update
        setLoadingChannels(false);
      }
    };

    void loadHomeChannels();

    return () => {
      cancelled = true;
      // Reset so the next effect run retries the load (e.g. after authData
      // is replaced by the backend profile sync in App.tsx)
      loadedAuthKeyRef.current = '';
    };
  }, [authData, effectiveDemo]);

  useEffect(() => {
    if (!homeChannels.length) return;

    setSelectedChannel((current) => {
      if (current) {
        const currentStillExists = homeChannels.some(
          (channel) => String(channel.stream_id || '') === String(current.stream_id || '')
        );
        if (currentStillExists) return current;
      }

      if (lastWatchedId) {
        const lastWatchedChannel = homeChannels.find((channel) => String(channel.stream_id || '') === lastWatchedId);
        if (lastWatchedChannel) return lastWatchedChannel;
      }

      return homeChannels[0] || null;
    });
  }, [homeChannels, lastWatchedId]);

  // Posiciona foco e janela virtual exatamente no canal que está tocando. O
  // ChannelList centraliza esse índice quando ele estiver fora da área visível.
  useEffect(() => {
    if (!homeChannels.length) { setFocusedChannelIndex(0); return; }
    if (selectedChannel) {
      const selectedId = String(selectedChannel.stream_id || '');
      const recentIdx = homeChannels.findIndex(
        (ch, index) => index >= favoriteCount && String(ch.stream_id || '') === selectedId
      );
      if (recentIdx >= 0) { setFocusedChannelIndex(recentIdx); return; }
      const favoriteIdx = homeChannels.findIndex((ch) => String(ch.stream_id || '') === selectedId);
      if (favoriteIdx >= 0) { setFocusedChannelIndex(favoriteIdx); return; }
    }
    setFocusedChannelIndex(0);
  }, [favoriteCount, homeChannels, selectedChannel]);

  // Activate a channel:
  //   - demo mode → redirect to Settings (activation required)
  //   - already playing in mini player → open LiveTV
  //   - not playing → start stream in mini player (stay on Home)
  const handleChannelActivate = useCallback((ch: XtreamContent) => {
    // In demo mode, demo channels are playable — don't redirect to Settings.
    // Only redirect if there's truly no content to play.
    if (effectiveDemo && !ch.direct_source) {
      onOpenSettings?.();
      return;
    }
    const isAlreadyPlaying =
      visibleSelectedChannel !== null &&
      String(ch.stream_id || '') === String(visibleSelectedChannel.stream_id || '');

    if (isAlreadyPlaying) {
      onOpenLiveTV?.({ selectedChannel: visibleSelectedChannel, channels, categories });
    } else {
      setSelectedChannel(ch);
    }
  }, [effectiveDemo, categories, channels, onOpenLiveTV, onOpenSettings, visibleSelectedChannel]);

  const handleOpenLiveTV = useCallback(() => {
    onOpenLiveTV?.({
      selectedChannel: visibleSelectedChannel,
      channels,
      categories,
    });
  }, [categories, channels, onOpenLiveTV, visibleSelectedChannel]);

  // Mantém a instância persistente da Live TV sincronizada enquanto ela é
  // exibida no retângulo do mini player da Home.
  useEffect(() => {
    if (!visibleSelectedChannel || !channels.length) return;
    onPreviewLiveTV?.({ selectedChannel: visibleSelectedChannel, channels, categories });
  }, [categories, channels, onPreviewLiveTV, visibleSelectedChannel]);

  // Keyboard: zone-aware navigation (topnav → contentnav → channels)
  useEffect(() => {
    const NAV_ICON_COUNT = HOME_NAV_ACTIONS.length;
    const navIds = contentNavIds;

    const onKeyDown = (event: KeyboardEvent) => {
      // Check ref (updated synchronously on every render) so this guard is
      // always current even if the effect hasn't re-run yet.
      if (pausedRef.current) return;
      const { key } = event;

      if (focusZone === 'channels') {
        if (key === 'Escape' || key === 'GoBack' || key === 'BrowserBack') {
          event.preventDefault();
          onRequestExit?.();
          return;
        }
        if (key === 'ArrowUp') {
          event.preventDefault();
          if (focusedChannelIndex === 0) {
            setFocusZone('contentnav');
            setContentnavFocusedId(activeNavItem);
          } else {
            setFocusedChannelIndex((prev) => prev - 1);
          }
          return;
        }
        if (key === 'ArrowDown') {
          event.preventDefault();
          setFocusedChannelIndex((prev) => Math.min(homeChannels.length - 1, prev + 1));
          return;
        }
        if (key === 'ArrowLeft' || key === 'ArrowRight') {
          event.preventDefault();
          setFocusZone('contentnav');
          setContentnavFocusedId('games');
          return;
        }
        if (key === 'Enter') {
          event.preventDefault();
          const focused = homeChannels[focusedChannelIndex];
          if (focused) handleChannelActivate(focused);
        }
        return;
      }

      if (focusZone === 'contentnav') {
        if (key === 'Escape' || key === 'GoBack' || key === 'BrowserBack') {
          event.preventDefault();
          onRequestExit?.();
          return;
        }
        if (key === 'ArrowUp') {
          event.preventDefault();
          setFocusZone('topnav');
          return;
        }
        if (key === 'ArrowDown') {
          event.preventDefault();
          setFocusZone('channels');
          return;
        }
        if (key === 'ArrowLeft') {
          event.preventDefault();
          const idx = navIds.indexOf(contentnavFocusedId);
          if (idx > 0) setContentnavFocusedId(navIds[idx - 1]);
          return;
        }
        if (key === 'ArrowRight') {
          event.preventDefault();
          const idx = navIds.indexOf(contentnavFocusedId);
          if (idx < navIds.length - 1) setContentnavFocusedId(navIds[idx + 1]);
          return;
        }
        if (key === 'Enter') {
          event.preventDefault();
          setActiveNavItem(contentnavFocusedId);
          setFocusZone('channels');
          if (contentnavFocusedId === 'movies') onOpenMovies?.();
          if (contentnavFocusedId === 'series') onOpenSeries?.();
          if (contentnavFocusedId === 'kids') onOpenKids?.();
          if (contentnavFocusedId === 'anime') onOpenAnime?.();
          if (contentnavFocusedId === 'games') onOpenSports?.();
          return;
        }
        return;
      }

      if (focusZone === 'topnav') {
        if (key === 'Escape' || key === 'GoBack' || key === 'BrowserBack') {
          event.preventDefault();
          onRequestExit?.();
          return;
        }
        if (key === 'ArrowDown') {
          event.preventDefault();
          setFocusZone('contentnav');
          return;
        }
        if (key === 'ArrowLeft') {
          event.preventDefault();
          // Skip index 0 (search) when it's hidden in kids mode
          const minIdx = isKidsMode ? 1 : 0;
          setTopnavFocusedIdx((prev) => Math.max(minIdx, prev - 1));
          return;
        }
        if (key === 'ArrowRight') {
          event.preventDefault();
          setTopnavFocusedIdx((prev) => Math.min(NAV_ICON_COUNT - 1, prev + 1));
          return;
        }
        if (key === 'Enter') {
          event.preventDefault();
          const action = HOME_NAV_ACTIONS[topnavFocusedIdx];
          if (action === NAV_ICON_SEARCH_INDEX) {
            if (!isKidsMode) onOpenSearch?.();
          }
          if (action === NAV_ICON_FAVORITES_INDEX) {
            onOpenFavorites?.();
          }
          if (action === NAV_ICON_SETTINGS_INDEX) {
            onOpenSettings?.();
          }
          if (action === NAV_ICON_LOCK_INDEX) {
            handleLockIconClick();
          }
          if (action === NAV_ICON_HISTORY_INDEX) {
            onOpenHistory?.();
          }
          if (action === NAV_ICON_REFRESH_INDEX) {
            onUpdateContent?.();
          }
          return;
        }
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    // paused intentionally omitted — checked via pausedRef which is always current
    focusZone, topnavFocusedIdx, contentnavFocusedId, activeNavItem,
    homeChannels, focusedChannelIndex, handleChannelActivate, isKidsMode, contentNavIds,
    handleLockIconClick, onOpenFavorites, onOpenMovies, onOpenSeries, onOpenKids, onOpenAnime, onOpenSports, onOpenSettings, onOpenSearch, onOpenHistory, onUpdateContent, onRequestExit,
  ]);

  return (
    <div className="home">
      <TopNavbar
        focusedIndex={focusZone === 'topnav' ? topnavFocusedIdx : undefined}
        onIconClick={(i) => {
          if (i === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
          if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
          if (i === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
          if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
          if (i === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
          if (i === NAV_ICON_REFRESH_INDEX) onUpdateContent?.();
        }}
      />

      <ContentNav
        activeId={activeNavItem}
        focusedId={focusZone === 'contentnav' ? contentnavFocusedId : undefined}
        onSelect={(id) => {
          setActiveNavItem(id);
          if (id === 'movies') onOpenMovies?.();
          if (id === 'series') onOpenSeries?.();
          if (id === 'kids') onOpenKids?.();
          if (id === 'anime') onOpenAnime?.();
          if (id === 'games') onOpenSports?.();
        }}
      />

      <div className="home__body">
        {/* Sidebar: lista de canais */}
        <ChannelList
          channels={homeChannels}
          focusedIndex={focusedChannelIndex}
          favoriteShortcutCount={favoriteCount}
          onSelect={handleChannelActivate}
          loading={loadingChannels}
        />

        {/* Área principal: player + EPG */}
        <div className="home__main">
          <div
            className="miniplayer home__player-slot"
            onClick={handleOpenLiveTV}
            role="button"
            tabIndex={0}
            aria-label={t('liveTV.openLiveTV')}
          >
            {!visibleSelectedChannel && (
              <div className="miniplayer__placeholder">
                <FiTv className="miniplayer__icon" />
                <span className="miniplayer__hint">{t('liveTV.loadingChannel')}</span>
              </div>
            )}
          </div>
          <EpgBar channel={visibleSelectedChannel} />
        </div>
      </div>

      {effectiveDemo && (
        <div className="home__demo-banner">
          {t('home.demoBanner')}
        </div>
      )}

      {lockModalOpen && (
        <PasswordModal
          mode={lockModalMode}
          error={t(lockModalError)}
          onConfirm={handleLockPasswordConfirm}
          onCancel={handleLockPasswordCancel}
        />
      )}
    </div>
  );
};
